/**
 * @file subscriber.component.ts
 * @author Codeforce
 * @date 23 Mar 2020
 * @copyright 2020 Bisnode Deutschland GmbH
 * @brief functions/Variables for the frontend (subscriber) component
 */
import { Component, Inject, OnInit } from "@angular/core";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import {FormBuilder, Validators, FormGroup, ReactiveFormsModule} from "@angular/forms";
import { RegisterService } from "../services/register.service";
import { Md5 } from "ts-md5/dist/md5";
import { UUID } from "angular2-uuid";
import { CustomDatePipe } from "../shared/custom.datepipe";
import { DeviceDetectorService } from "ngx-device-detector";
import { PLATFORM_ID } from "@angular/core";
import { environment } from "../environments/environment";
import {CommonModule} from "@angular/common";

@Component({
  selector: "app-subscriber",
  templateUrl: "./subscriber.component.html",
  styleUrls: ["./subscriber.component.css"],
  imports: [
    CommonModule,
    ReactiveFormsModule
  ],
  standalone: true
})
export class SubscriberComponent implements OnInit {
  token: string;
  title = "amc-newsletter";
  private countryCode = "de";
  ipAddress = "";
  newsletterImage = "";
  stickImage = "";
  deButton = "";
  amcLogoImage = "";
  fieldLabel;
  fieldLabel2;
  amcContactform: FormGroup;
  amcContactformLabel: FormGroup;
  translateData = [];
  language = { key: ["de"] };
  email = "";
  httpProtocol = "";
  supplierId = "99";
  regEx = "^[a-üA-Ü -]+";

  /**
   * @brief getFieldContent()
   * @param field - from the frontend form
   * @return content of a specific field
   * @details function to get the content of a specific field
   */
  getFieldContent(field) {
    return this.amcContactform.get(field);
  }

  /**
   * @brief getFieldLabel()
   * @param label - from the frontend form
   * @return translated text of the label
   * @details function to get the translated text of the label, from the database
   */
  getFieldLabel(label) {
    this.fieldLabel2 = this.amcContactformLabel.get(label);
    return this.fieldLabel2 != null ? this.fieldLabel2.value : "";
  }

  /**
   * @brief generateEmailText()
   * @param -
   * @return HTML formatted Email Text
   * @details function to generate a HTML formatted Text, in order to send a confirmation email to a subscriber
   */
  generateEmailText() {
    this.email =
      "<!DOCTYPE html><html>" +
      "<head>" +
      '<meta http-equiv="Content-Type" content="text/html; charset=utf-8" />' +
      '<meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">' +
      '<link rel="stylesheet" href="' +
      this.getHref(false) +
      "assets/css/fonts-paneuropean.css" +
      '">' +
      "<style>" +
      "body{" +
      "font-family: 'HelveticaNeue', Arial, sans-serif !important;" +
      "font-size: 18px;" +
      "}" +
      ".button a {" +
      "text-decoration: none !important;" +
      "outline: none;" +
      "cursor: pointer;" +
      "}" +
      ".button a:hover, .button a:focus{" +
      "color: #ffffff !important;" +
      "text-decoration: none !important;" +
      "}" +
      ".button {" +
      "background-color: #A0AF37;" +
      "padding: 15px 32px;" +
      "border:none;" +
      "border-radius: 20px;" +
      "cursor: pointer;" +
      "font-size: 16px;" +
      "padding: 10px 32px;" +
      "margin: 4px 2px;" +
      "}" +
      "</style>" +
      "</head>" +
      '<body align="center">' +
      '<div class="container">' +
      '<table width="600" align="center">' +
      '<tr><td align="center"><img width="120" src="' +
      this.getHref(false) +
      "assets/images/amc_logo_" +
      this.countryCode +
      ".png" +
      '" alt="AMC"></td></tr>' +
      '<tr><td align="center">' +
      "<div>" +
      "<br><br>" +
      '<p align="center">' +
      this.getFieldLabel("em_welcome_1") +
      "</p>" +
      '<p align="center">&nbsp;</p>' +
      '<p align="center">' +
      this.getFieldLabel("em_welcome_2") +
      "</p>" +
      '<p align="center">' +
      this.getFieldLabel("em_welcome_3") +
      "</p>" +
      '<p align="center">' +
      this.getFieldLabel("em_welcome_4") +
      "</p>" +
      '<p align="center">&nbsp;</p>' +
      '<p align="center"><strong>' +
      this.getFieldLabel("em_welcome_5") +
      "</strong></p>" +
      "</div>" +
      "</td></tr>" +
      '<tr><td align="center">' +
      '<a align="center" href="' +
      this.getHref(true) +
      '" action="confirm" >' +
      '<img align="center" width="325" height="65" src="' +
      this.getHref(false) +
      "assets/images/" +
      this.countryCode +
      "button.png" +
      '" alt="nl_confirm">' +
      "</a>" +
      "<tr></tr>" +
      "</table>" +
      "</div>" +
      "</body>" +
      "</html>";
  }

  constructor(
    private router: Router,
    private registerService: RegisterService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private _platformId: Object
  ) {
    this.newsletterImage = environment.newsletterImage;
    this.stickImage = environment.stickImage;
    this.deButton = environment.amcLogoImage;
    this.amcLogoImage = environment.amcLogoImage;

    this.httpProtocol = environment.httpProtocol;
  }

  ngOnInit() {
    this.token = this.generateCustomToken();

    this.route.paramMap.subscribe((params: ParamMap) => {
      this.countryCode = params.get("lang");
    });

//    debugger;

    if (this.countryCode == null) {
      this.countryCode = "de";
    }

    /**
     * Gets supplierId
     */
    switch (this.countryCode.toUpperCase()) {
      case "SK-SK":
        this.regEx =
          "^[a-zA-Z - Á á Ä ä Č č Ď ď É é Í í Ĺ ĺ Ľ ľ Ň ň Ó ó Ô ô Ŕ ŕ Š š Ť ť Ú ú Ý ý Ž ž]+";
        break;
      default:
        break;
    }
    this.loadTranslateDataFromDB(this.countryCode);

    /**
     * Definition of form field-labels for frontend
     */
    this.amcContactformLabel = this.formBuilder.group({
      nl_email: ["E-Mail"],
      nl_submit: ["Anmelden"],
      nl_salutation: ["Anrede"],
      nl_salutation_choise: ["Bitte wählen . . ."],
      nl_salutation_male: ["Herr"],
      nl_salutation_female: ["Frau"],
      nl_firstname: ["Vorname"],
      nl_surname: ["Nachname"],
      nl_street: ["Straße und Hausnummer"],
      nl_postalcode: ["Postleitzahl"],
      nl_area: ["Ort"],
      nl_mobile: ["Mobiltelefon"],
      nl_agreement: ["Ich stimme den"],
      nl_privacy_policy: ["Datenschutzerklärungen"],
      nl_privacy_policy_text: ["zu."],
      nl_heading: ["AMC News"],
      nl_heading_content_1: ["Immer frisch für Sie zubereitet:"],
      nl_heading_content_2: [
        "die AMC News mit leckeren Rezeptideen, Produktnews und alles andere rund um’s genussvolle und gesunde Kochen mit AMC!",
      ],
      nl_heading_content_3: ["Jetzt abonnieren – so geht’s:"],
      nl_heading_content_4: ["Formular ausfüllen &amp; abschicken"],
      nl_heading_content_5: ["Bestätigungsemail öffnen"],
      nl_heading_content_6: [
        "Bestätigungslink in der E-Mail öffnen - und schon erhalten Sie den AMC Newsletter!",
      ],
      nl_warning_missing: [""],
      nl_err: ["Bitte überprüfen Sie Ihre Eingabe"],
      nl_footing_content_1: [
        "Bei Anmeldung zu den AMC News werden Ihre personenbezogenen Daten für eigene Werbezwecke genutzt, bis Sie sich von den AMC News abmelden. Die Abmeldung ist jederzeit möglich.",
      ],
      nl_footing_content_2: ["Mit * gekennzeichnete Felder sind Pflichtfelder"],
      nl_warning_mail_exists: [""],
      nl_back_btn: ["Zurück"],
      href_data_protection: [
        "https://www.amc.info/de-de/datenschutzerklaerung",
      ],
      em_welcome_1: ["Hallo!"],
      em_welcome_2: ["Bitte best&auml;tigen Sie Ihre Anmeldung"],
      em_welcome_3: [
        "um unsere <strong>aktuellsten</strong> <strong>News, Rezepte, Geniesser Tipps&nbsp;&amp; Tricks",
      ],
      em_welcome_4: ["zu erhalten."],
      em_welcome_5: ["Sind Sie bereit, Inspiration zu tanken?"],
      em_confirm: ["Anmeldung best&auml;tigen"],
      nl_err_btn: ["Bitte überprüfen Sie Ihre Eingabe"],
      nl_email_subject: ["AMC News Letzter Schritt"],
      nl_supplierId: ["99"],
      nl_regex: [""],
    });

    /**
     * Definition of form fields for subscriber
     */
    this.amcContactform = this.formBuilder.group({
      oo_classId: ["4"],
      email: [
        "",
        [Validators.required, Validators.maxLength(255), Validators.email],
      ],
      gender: ["0"],
      firstname: [
        "",
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(255),
          Validators.pattern(this.regEx),
        ],
      ],
      lastname: [
        "",
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(255),
          Validators.pattern(this.regEx),
        ],
      ],
      street: [
        "",
        [
          Validators.required,
          Validators.minLength(5),
          Validators.maxLength(255),
        ],
      ],
      zip: [
        "",
        [
          Validators.required,
          Validators.pattern("^([0]{1}[1-9]{1}|[1-9]{1}[0-9]{1})[0-9]{2,3}$"),
        ],
      ],
      area: [
        "",
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(255),
          Validators.pattern(this.regEx),
        ],
      ],
      mobilenumber: [
        "",
        [
          Validators.pattern(
            "[0-9]*\\/*(\\+49)*[ ]*(\\([0-9]+\\))*([ ]*(-|–)*[ ]*[0-9]+)*"
          ),
        ],
      ],
      cbAgreement: [false, [Validators.required]],
      token: [this.token],
      submittedTime: [this.getCustomDate()],
      doiOptInOutTime: [""],
      subscriberLanguage: [this.countryCode],
      medium: ["newsletter"],
      browser: [this.getBrowserInfo()],
      ipSubmitted: [this.ipAddress],
      ipDoiOptInOut: [],
      isNewsletter: ["0"],
      campaignCode: [""],
      treatmentCode: [""],
      individualHash: [""],
      countryNumber: [this.countryCode],
      supplierId: [this.getFieldLabel("nl_supplierId")],
      document: [""],
      subject: [""],
      active: ["1"],
    });

    this.registerService.getIPAddress().subscribe((res) => {
      this.amcContactform.patchValue({ ipSubmitted: res["ip"] });
    });
  }

  /**
   * Submit registration data
   * 1.) Generate Email text
   * 2.) Send Data to database
   */
  onSubmit() {
    if (
      this.amcContactform.valid &&
      this.getFieldContent("cbAgreement").value
    ) {
      this.generateEmailText();
      this.amcContactform.patchValue({ document: this.email });
      this.amcContactform.patchValue({
        subject: this.getFieldLabel("nl_email_subject"),
      });
      this.amcContactform.patchValue({
        supplierId: this.getFieldLabel("nl_supplierId"),
      });

      this.registerService.subscribe(this.amcContactform.value).subscribe(
        (response) => {
          if (response["response"] == "exists") {
            this.router.navigate(["./" + this.countryCode + "/error/email"]);
          } else if (response["response"] == "success") {
            this.router.navigate(["./" + this.countryCode + "/success"]);
          } else {
            this.router.navigate(["./" + this.countryCode + "/error/error"]);
          }
        },
        (error) =>
          this.router.navigate(["./" + this.countryCode + "/error/error"])
      );
    }
  }

  /**
   * Get translated data from the database from register.service.ts
   * @param language
   */
  loadTranslateDataFromDB(language) {
    this.translateData = [];
    this.language.key = language;
    this.registerService.loadTranslateDataFromDB(this.language.key).subscribe(
      (data) => {
        this.translateData = data;
        this.setTranslate();
      },
      (error) => {
        this.router.navigate(["./" + this.countryCode + "/error/error"]);
      }
    );
  }

  /**
   * Set labels with translated texts from the database
   */
  setTranslate() {
    this.translateData.forEach((obj) => {
      this.fieldLabel = this.translateData.find((k) => k.key === obj.key);
      if (
        this.fieldLabel != null &&
        this.amcContactformLabel.get(obj.key) != null
      ) {
        this.amcContactformLabel.controls[obj.key].setValue(
          this.fieldLabel.text
        );
      }
    });
  }

  /**
   * get custom date format for database
   */
  getCustomDate(dateFormat?: any) {
    let customDatePipe = new CustomDatePipe("en-US");
    return customDatePipe.transform(new Date(), dateFormat);
  }

  /**
   * Generate a custom Token
   */
  generateCustomToken() {
    let uuid = UUID.UUID();
    const md5 = new Md5();
    return md5.appendStr(uuid).end().toString();
  }

  /**
   * get Browser Information of the subscriber
   */
  getBrowserInfo() {
    let deviceService = new DeviceDetectorService(this._platformId);
    return deviceService.browser + " V[" + deviceService.browser_version + "]";
  }

  /**
   * get URL Information of the subscriber
   */
  public getHref(bSubscribe) {
    let ret = "";
    let hostname = window.location.hostname;
    let port = window.location.port;

    if (port != "") {
      hostname = hostname + ":" + port;
    }

    ret = this.httpProtocol + hostname + "/amc-forms-web/";

    if (bSubscribe) {
      ret =
        this.httpProtocol +
        hostname +
        "/amc-forms-web/" +
        this.language.key +
        "/anmeldung?token=" +
        this.token;
    }
    return ret;
  }
}
